import React, { useState, useEffect } from 'react';
import { LogIn, User, Key, Loader2, Eye, EyeOff, ArrowLeft, Settings as SettingsIcon, Server, ChevronDown, ChevronUp } from 'lucide-react';
import { LOCKED_SERVER_URL, xtreamAPI } from '@/services/xtream-api';
import { useAppStore } from '@/store/app-store';
import { PROVIDERS, resolveProviders } from '@/config/providers';
import toast from 'react-hot-toast';

const STORAGE_KEY = 'redhood_saved_credentials';

// Loose URL match (ignore scheme + trailing slash) to map a saved line to a provider.
const normUrl = (u: string) => u.trim().replace(/\/+$/, '').replace(/^https?:\/\//i, '').toLowerCase();

export const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Named provider selection (RedHood A/B/C). Default to the first provider.
  const [providerId, setProviderId] = useState(PROVIDERS[0].id);
  // Bring-your-own-provider (Advanced): paying users can point at their own line.
  const [useOwnProvider, setUseOwnProvider] = useState(false);
  const [customServerUrl, setCustomServerUrl] = useState('');

  const {
    setCredentials,
    setAuthInfo,
    setLoginRequested,
    resellerMode,
    setAdminPanelOpen,
    setServerUrl,
    resetServerUrl,
    providerUrls,
    allowOwnProvider,
  } = useAppStore();

  // Providers with any admin overrides applied (labels shown; URLs stay internal).
  const providers = React.useMemo(() => resolveProviders(providerUrls), [providerUrls]);

  // Load saved credentials on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const credentials = JSON.parse(saved);
        setUsername(credentials.username || '');
        setPassword(credentials.password || '');
        setRememberMe(true);
        // Restore the previously-used server: a named provider if it matches one,
        // otherwise a custom (Advanced) line.
        if (credentials.serverUrl) {
          const match = resolveProviders(useAppStore.getState().providerUrls).find(
            (p) => normUrl(p.url) === normUrl(credentials.serverUrl)
          );
          if (match) {
            setProviderId(match.id);
          } else if (
            credentials.serverUrl !== LOCKED_SERVER_URL &&
            useAppStore.getState().allowOwnProvider
          ) {
            setUseOwnProvider(true);
            setCustomServerUrl(credentials.serverUrl);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load saved credentials:', error);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !password) {
      toast.error('Please enter your username and password');
      return;
    }

    const usingCustom = allowOwnProvider && useOwnProvider && customServerUrl.trim().length > 0;
    if (allowOwnProvider && useOwnProvider && !customServerUrl.trim()) {
      toast.error('Enter your provider server URL, or turn off Advanced');
      return;
    }

    setIsLoading(true);

    try {
      // Use the chosen named provider, or an Advanced custom URL.
      const provider = providers.find((p) => p.id === providerId) || providers[0];
      const targetUrl = usingCustom ? customServerUrl.trim() : provider.url;
      xtreamAPI.setServerUrl(targetUrl);
      if (normUrl(targetUrl) === normUrl(LOCKED_SERVER_URL)) {
        resetServerUrl();
      } else {
        setServerUrl(targetUrl);
      }

      const credentials = {
        serverUrl: targetUrl,
        username,
        password,
      };
      const success = await xtreamAPI.setCredentials(credentials);

      if (success) {
        const authInfo = xtreamAPI.getUserInfo();
        const actualUrl = xtreamAPI.getActualServerUrl();

        setCredentials(credentials);
        if (authInfo) {
          setAuthInfo(authInfo);
        }

        if (rememberMe) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(credentials));
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }

        if (actualUrl && actualUrl !== LOCKED_SERVER_URL) {
          console.log(`URL redirect: ${LOCKED_SERVER_URL} -> ${actualUrl}`);
        }

        toast.success('Login successful!');
      } else {
        toast.error('Authentication failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || 'Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/hero-image.jpeg)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-dark-900/90 to-black/80 backdrop-blur-sm" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-md">
        {resellerMode && (
          <button
            onClick={() => setLoginRequested(false)}
            className="mb-4 inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors focus-strong"
          >
            <ArrowLeft size={18} />
            Back to catalog
          </button>
        )}
        <div className="bg-dark-800/95 backdrop-blur-md rounded-2xl shadow-2xl p-8 border border-dark-700/50">
          {/* Logo/Title */}
          <div className="text-center mb-8">
            {/* Hero Image as Logo */}
            <div className="inline-block mb-4">
              <img
                src="/hero-image.jpeg"
                alt="RedHood IPTV"
                className="w-24 h-24 rounded-full object-cover border-4 border-primary-600 shadow-lg shadow-primary-600/50"
              />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">RedHood IPTV</h1>
            <p className="text-gray-400">Sign in to start streaming</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Server selector (named providers; URLs never shown) */}
            {providers.length > 1 && !useOwnProvider && (
              <div>
                <label htmlFor="server" className="block text-sm font-medium text-gray-300 mb-2">
                  Server
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Server className="text-gray-500" size={20} />
                  </div>
                  <select
                    id="server"
                    value={providerId}
                    onChange={(e) => setProviderId(e.target.value)}
                    disabled={isLoading}
                    className="w-full pl-10 pr-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                  >
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="text-gray-500" size={20} />
                </div>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="w-full pl-10 pr-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key className="text-gray-500" size={20} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-12 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-300 transition-colors"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-primary-600 bg-dark-700 border-dark-600 rounded focus:ring-primary-500 focus:ring-2"
                disabled={isLoading}
              />
              <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-300">
                Remember me
              </label>
            </div>

            {/* Advanced: bring your own provider. Operator-gated — disabled until
                an admin enables it in Operator settings. */}
            <div className="rounded-lg border border-dark-600 bg-dark-700/40">
              <button
                type="button"
                onClick={() => allowOwnProvider && setUseOwnProvider((v) => !v)}
                disabled={!allowOwnProvider}
                title={allowOwnProvider ? undefined : 'Enabled by the operator'}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors focus-strong ${
                  allowOwnProvider
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-500 cursor-not-allowed opacity-60'
                }`}
                aria-expanded={useOwnProvider}
              >
                <span className="flex items-center gap-2">
                  <Server size={16} />
                  Use my own provider (Advanced)
                </span>
                {!allowOwnProvider ? (
                  <span className="text-[10px] uppercase tracking-wide text-gray-500">Operator only</span>
                ) : useOwnProvider ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>

              {allowOwnProvider && useOwnProvider && (
                <div className="px-3 pb-3 space-y-2">
                  <label htmlFor="serverUrl" className="block text-xs font-medium text-gray-400">
                    Provider server URL
                  </label>
                  <input
                    type="url"
                    id="serverUrl"
                    inputMode="url"
                    autoComplete="off"
                    value={customServerUrl}
                    onChange={(e) => setCustomServerUrl(e.target.value)}
                    placeholder="http://your-provider.example:8080"
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={isLoading}
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] text-gray-500">
                      For subscribers using their own Xtream Codes line.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setCustomServerUrl('');
                        setUseOwnProvider(false);
                        resetServerUrl();
                      }}
                      className="text-[11px] text-gray-400 hover:text-white underline transition-colors focus-strong"
                    >
                      Reset to default
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Connecting...
                </>
              ) : (
                <>
                  <LogIn size={20} />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-400">
            <p>Use your Xtream Codes account to access RedHood IPTV</p>
          </div>

          {/* Operator / Admin — PIN-gated, accessible before signing in so the
              provider URL and branding can be configured without an account. */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setAdminPanelOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors focus-strong"
            >
              <SettingsIcon size={14} />
              Operator / Admin
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
